import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import API from "../lib/api";
import { Search } from "lucide-react";
import logger from "../lib/logger";

export default function Collection() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [tags, setTags] = useState([]);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const tag = params.get("tag") || "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/public/collection", { params: { q, category, tag, limit: 48 } });
        setItems(data.items || []);
        setTotal(data.total || 0);
        setCats(data.categories || []);
        setTags(data.tags || []);
      } catch (e) { logger.error("Collection load failed:", e); }
    })();
  }, [q, category, tag]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="bg-parchment py-14">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-muted-h mb-2">Public Collection</div>
          <h1 className="font-display text-5xl text-ink mb-3">Heritage Collection</h1>
          <p className="text-lg text-ink-soft max-w-2xl">Explore objects, stories, and documents shared and validated by our community.</p>
          <div className="mt-6 max-w-xl relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-h" />
            <input
              type="search"
              placeholder="Search titles, descriptions, and context…"
              defaultValue={q}
              onKeyDown={(e) => { if (e.key === "Enter") setParam("q", e.target.value); }}
              className="input-heritage pl-11"
              data-testid="collection-search"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-[220px_1fr] gap-10 flex-1 w-full">
        <aside className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-h mb-3">Categories</div>
            <div className="space-y-1">
              <button onClick={() => setParam("category", "")} className={`block text-left w-full px-2 py-1 rounded ${!category ? "text-terracotta font-semibold" : "text-ink-soft hover:text-terracotta"}`} data-testid="cat-all">All</button>
              {cats.map((c) => (
                <button key={c} onClick={() => setParam("category", c)} className={`block text-left w-full px-2 py-1 rounded ${category === c ? "text-terracotta font-semibold" : "text-ink-soft hover:text-terracotta"}`} data-testid={`cat-${c}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          {tags.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-h mb-3">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {tags.slice(0, 20).map((t) => (
                  <button key={t} onClick={() => setParam("tag", tag === t ? "" : t)}
                    className={`text-xs px-2 py-1 rounded-full border ${tag === t ? "bg-[var(--heritage-terracotta)] text-white border-[var(--heritage-terracotta)]" : "bg-[var(--heritage-white)] border-heritage text-ink-soft hover:border-[var(--heritage-terracotta)]"}`}
                    data-testid={`tag-${t}`}>{t}</button>
                ))}
              </div>
            </div>
          )}
        </aside>

        <div>
          <div className="text-sm text-muted-h mb-4">{total} item{total === 1 ? "" : "s"} found</div>
          {items.length === 0 ? (
            <div className="card-heritage text-center py-12">
              <div className="font-display text-2xl text-ink mb-2">No items yet</div>
              <p className="text-ink-soft">Approved heritage items will appear here as the collection grows.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((it) => (
                <Link key={it.id} to={`/collection/${it.id}`} className="card-heritage hover:shadow-md hover:-translate-y-0.5 transition block" data-testid={`col-item-${it.id}`}>
                  <div className="text-[11px] uppercase tracking-widest text-gold mb-2">{it.category || "Heritage"}</div>
                  <h3 className="font-display text-xl text-ink mb-2 line-clamp-2">{it.title}</h3>
                  <p className="text-sm text-ink-soft line-clamp-3">{it.description}</p>
                  <div className="mt-3 text-xs text-muted-h">{it.location} · {it.relevant_dates}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
